// 🔧 settings_images.js
// ✅ Purpose: Admin-only Event Images Settings page
// ✅ Features:
// - Standalone replacement for old Images tab
// - Upload/delete event logo
// - Upload/delete QR card background image
// - Saves image URLs to /events/{eventId}/settings/voting
// - Compresses uploaded images
// - Shows image previews, dimensions, and file size
// - Uses standard admin layout
// - Shows success/error notifications at the top of the page
// - Auto-clears notifications after 20 seconds

import React, { useEffect, useRef, useState } from "react";
import { doc, getDoc, setDoc, Timestamp } from "firebase/firestore";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { db, storage } from "../../firebase";
import { useEventContext } from "../../hooks/use_event_context";
import { useRole } from "../../role_context";
import { useIsEventManager } from "../../hooks/use_is_event_manager";
import FullPageSpinner from "../../components/FullPageSpinner";
import imageCompression from "browser-image-compression";
import { deleteImageFromUrl } from "../../utils/storage_helpers";
import { showStatus } from "../../utils/status_helpers";
import { logger } from "../../utils/logger";

const SettingsImages = () => {
  const { eventId, eventLoading, ready } = useEventContext({
    redirectIfNotFound: true,
  });

  const { role, eventRole } = useRole();
  const isManager = useIsEventManager(eventId);

  const fileInputRef = useRef(null);
  const logoInputRef = useRef(null);
  const mapInputRef = useRef(null);
  const [uploadProgress, setUploadProgress] = useState(0);  
  const [settings, setSettings] = useState(null);
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [eventMap, setEventMap] = useState("");
  const [mapFile, setMapFile] = useState(null);
  const [eventImage, setEventImage] = useState("");
  const [eventLogo, setEventLogo] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [imageInfo, setImageInfo] = useState({
    width: null,
    height: null,
    sizeKB: null,
  });
  const [saving, setSaving] = useState(false);

  const canAccess =
    ["admin", "manager"].includes(role) ||
    eventRole === "manager" ||
    isManager;

  useEffect(() => {
    if (!status?.message) return;

    const timer = setTimeout(() => {
      setStatus(null);
    }, 20000);

    return () => clearTimeout(timer);
  }, [status]);

  useEffect(() => {
    const loadSettings = async () => {
      if (!eventId) return;

      try {
        console.log("🖼 Loading event images for event:", eventId);

        const snap = await getDoc(
          doc(db, "events", eventId, "settings", "voting")
        );

        const data = snap.exists() ? snap.data() : {};

        setSettings(data);
        setEventImage(data.eventImage || "");
        setEventLogo(data.eventLogo || "");
        setEventMap(data.eventMap || "");
      } catch (err) {
        logger.error("❌ Failed to load event images:", {
          value: err,
        });

        setStatus({
          type: "error",
          message: "❌ Failed to load event images.",
        });
      } finally {
        setSettingsLoading(false);
      }
    };

    loadSettings();
  }, [eventId]);

  useEffect(() => {
    if (!eventImage) {
      setImageInfo({
        width: null,
        height: null,
        sizeKB: null,
      });

      return;
    }

    const img = new Image();
    img.src = eventImage;

    img.onload = async () => {
      let sizeKB = null;

      try {
        const res = await fetch(eventImage);
        const blob = await res.blob();

        sizeKB = (blob.size / 1024).toFixed(1);
      } catch (err) {
        logger.warn("⚠️ Failed to fetch image size:", {
          value: err,
        });
      }

      setImageInfo({
        width: img.naturalWidth,
        height: img.naturalHeight,
        sizeKB,
      });
    };
  }, [eventImage]);

  const uploadImage = async (file, path, maxSize = 1600) => {
    const ext = file.name.split(".").pop();
    const refPath = `${path}.${ext}`;
    const storageRef = ref(storage, refPath);

    const compressed = await imageCompression(file, {
      maxWidthOrHeight: maxSize,
      useWebWorker: true,
    });

    // ✅ Preserve/force image metadata after compression.
    // ✅ Firebase Storage Rules check request.resource.contentType.
    // ✅ Without metadata, compressed uploads may arrive as application/octet-stream and fail rules.
    const uploadMetadata = {
      contentType: compressed.type || file.type || "image/png",
    };

    const uploadTask = uploadBytesResumable(
      storageRef,
      compressed,
      uploadMetadata
    );

    return new Promise((resolve, reject) => {
      uploadTask.on(
        "state_changed",
        (snapshot) => {
          const percent = Math.round(
            (snapshot.bytesTransferred / snapshot.totalBytes) * 100
          );

          setUploadProgress(percent);
        },
        (error) => reject(error),
        async () => {
          const url = await getDownloadURL(uploadTask.snapshot.ref);
          resolve(url);
        }
      );
    });
  };

  const handleUpload = async () => {
    if (!imageFile && !logoFile && !mapFile) {
      showStatus(setStatus, {
        type: "warning",
        message: "Select an image or logo first.",
      });

      return;
    }

    let newImage = eventImage;
    let newLogo = eventLogo;
    let newMap = eventMap;

    setSaving(true);
    setUploadProgress(0);

    try {
      if (imageFile) {
        if (eventImage) {
          await deleteImageFromUrl(eventImage);
        }

        newImage = await uploadImage(
          imageFile,
          `eventAssets/${eventId}/settings/eventImage`,
          1600
        );
      }

      if (logoFile) {
        if (eventLogo) {
          await deleteImageFromUrl(eventLogo);
        }

        newLogo = await uploadImage(
          logoFile,
          `eventAssets/${eventId}/settings/eventLogo`,
          600
        );
      }

      // 🗺️ Upload/replace event map image
      if (mapFile) {
        if (eventMap) {
          await deleteImageFromUrl(eventMap);
        }

        newMap = await uploadImage(
          mapFile,
          `eventAssets/${eventId}/settings/eventMap`,
          2200
        );
      }

      const update = {
        eventImage: newImage,
        eventLogo: newLogo,
        eventMap: newMap,
        lastSaved: Timestamp.now(),
      };

      await setDoc(
        doc(db, "events", eventId, "settings", "voting"),
        update,
        { merge: true }
      );

      setEventImage(newImage);
      setEventLogo(newLogo);
      setEventMap(newMap);
      setSettings((prev) => ({
        ...prev,
        ...update,
      }));

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }

      if (logoInputRef.current) {
        logoInputRef.current.value = "";
      }

      if (mapInputRef.current) {
        mapInputRef.current.value = "";
      }

      setImageFile(null);
      setLogoFile(null);
      setMapFile(null);

      showStatus(setStatus, {
        type: "success",
        message: "Images saved successfully.",
      });
    } catch (err) {
      logger.error("❌ Upload failed:", {
        value: err,
      });

      showStatus(setStatus, {
        type: "error",
        message: "Failed to upload images.",
      });
    } finally {
      setUploadProgress(0);
      setSaving(false);
    }
  };

  const handleDeleteImage = async () => {
    if (!eventImage) return;

    if (!window.confirm("Delete the uploaded event background image?")) {
      return;
    }

    try {
      await deleteImageFromUrl(eventImage);

      const update = {
        eventImage: "",
        lastSaved: Timestamp.now(),
      };

      await setDoc(
        doc(db, "events", eventId, "settings", "voting"),
        update,
        { merge: true }
      );

      setEventImage("");
      setSettings((prev) => ({
        ...prev,
        ...update,
      }));

      showStatus(setStatus, {
        type: "warning",
        message: "Background image deleted.",
      });
    } catch (err) {
      logger.error("❌ Failed to delete background image:", {
        value: err,
      });

      showStatus(setStatus, {
        type: "error",
        message: "Failed to delete image.",
      });
    }
  };

  const handleDeleteLogo = async () => {
    if (!eventLogo) return;

    if (!window.confirm("Delete the uploaded event logo?")) {
      return;
    }

    try {
      await deleteImageFromUrl(eventLogo);

      const update = {
        eventLogo: "",
        lastSaved: Timestamp.now(),
      };

      await setDoc(
        doc(db, "events", eventId, "settings", "voting"),
        update,
        { merge: true }
      );

      setEventLogo("");
      setSettings((prev) => ({
        ...prev,
        ...update,
      }));

      showStatus(setStatus, {
        type: "warning",
        message: "Logo deleted.",
      });
    } catch (err) {
      logger.error("❌ Failed to delete logo:", {
        value: err,
      });

      showStatus(setStatus, {
        type: "error",
        message: "Failed to delete logo.",
      });
    }
  };

  // 🗺️ Deletes the uploaded public event map image
  const handleDeleteMap = async () => {
    if (!eventMap) return;

    if (!window.confirm("Delete the uploaded event map image?")) {
      return;
    }

    try {
      await deleteImageFromUrl(eventMap);

      const update = {
        eventMap: "",
        lastSaved: Timestamp.now(),
      };

      await setDoc(
        doc(db, "events", eventId, "settings", "voting"),
        update,
        { merge: true }
      );

      setEventMap("");
      setSettings((prev) => ({
        ...prev,
        ...update,
      }));

      showStatus(setStatus, {
        type: "warning",
        message: "Event map deleted.",
      });
    } catch (err) {
      logger.error("❌ Failed to delete event map:", {
        value: err,
      });

      showStatus(setStatus, {
        type: "error",
        message: "Failed to delete event map.",
      });
    }
  };

  const lastSavedText = settings?.lastSaved?.toDate?.()
    ? settings.lastSaved.toDate().toLocaleString()
    : "Never";

  if (!ready || eventLoading || settingsLoading || isManager === null) {
    return <FullPageSpinner message="Loading event images..." />;
  }

  if (!canAccess) {
    return (
      <div className="container py-5">
        <div className="alert alert-danger">
          You do not have permission to access this page.
        </div>
      </div>
    );
  }

  return (
    <section id="top-content" className="container-fluid py-5">
      <div className="container" style={{ maxWidth: "1200px" }}>
        <div className="row mb-4">
          <div className="col-12 col-lg-8">
            <h1 className="display-5 mb-2">🖼 Event Images</h1>

            <p className="lead text-muted mb-0">
              Manage event branding images used throughout the public event experience,
              QR cards, sponsor displays, and marketing materials.
            </p>
          </div>

          <div className="col-12 col-lg-4 mt-3 mt-lg-0 d-flex justify-content-lg-end align-items-start">
            <button
              className="btn btn-primary"
              onClick={handleUpload}
              disabled={saving || (!imageFile && !logoFile && !mapFile)}
            >
              {saving ? "Saving..." : "💾 Save Images"}
            </button>
          </div>
        </div>

        {status?.message && (
          <div
            className={`alert ${
              status.type === "success"
                ? "alert-success"
                : status.type === "warning"
                ? "alert-warning"
                : status.type === "error"
                ? "alert-danger"
                : "alert-info"
            }`}
          >
            {status.message}
          </div>
        )}

        {saving && uploadProgress > 0 && uploadProgress < 100 && (
          <div className="mb-4">
            <label className="form-label fw-bold">Upload Progress</label>

            <div className="progress">
              <div
                className="progress-bar progress-bar-striped progress-bar-animated"
                role="progressbar"
                style={{ width: `${uploadProgress}%` }}
                aria-valuenow={uploadProgress}
                aria-valuemin="0"
                aria-valuemax="100"
              >
                {uploadProgress}%
              </div>
            </div>
          </div>
        )}

        <div className="card shadow-sm mb-4">
          <div className="card-header bg-white">
            <strong>Image Guidelines</strong>
          </div>

          <div className="card-body">
            <div className="row g-3">
              <div className="col-12 col-md-6">
                <div className="border rounded p-3 h-100">
                  <div className="fw-bold mb-2">
                    🏷 Event Logo
                  </div>

                  <div className="small text-muted">
                    Used throughout event pages, sponsor pages, dashboards,
                    marketing materials, and branded content.
                  </div>
                </div>
              </div>

              <div className="col-12 col-md-6">
                <div className="border rounded p-3 h-100">
                  <div className="fw-bold mb-2">
                    🖼 QR Card Background
                  </div>

                  <div className="small text-muted">
                    Used behind QR cards and printed event materials when QR image
                    backgrounds are enabled.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="card shadow-sm mb-4">
          <div className="card-header bg-white">
            <strong>🏷 Event Logo</strong>
          </div>

          <div className="card-body">
            {eventLogo ? (
              <div className="text-center mb-4">
                <img
                  src={eventLogo}
                  alt="Event Logo"
                  className="img-fluid border rounded"
                  style={{
                    maxHeight: "180px",
                    objectFit: "contain",
                  }}
                />
              </div>
            ) : (
              <div className="alert alert-light text-center">
                No event logo uploaded.
              </div>
            )}

            <div className="row g-3 align-items-end">
              <div className="col-12 col-lg-8">
                <label className="form-label">
                  Upload Event Logo
                </label>

                <input
                  type="file"
                  accept="image/*"
                  className="form-control"
                  ref={logoInputRef}
                  onChange={(e) => setLogoFile(e.target.files[0])}
                />
              </div>

              <div className="col-12 col-lg-4">
                {eventLogo && (
                  <button
                    className="btn btn-outline-danger w-100"
                    onClick={handleDeleteLogo}
                  >
                    🗑 Delete Logo
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="card shadow-sm mb-4">
          <div className="card-header bg-white">
            <strong>🖼 QR Card Background Image</strong>
          </div>

          <div className="card-body">
            {eventImage ? (
              <>
                <div className="text-center mb-4">
                  <img
                    src={eventImage}
                    alt="Event Background"
                    className="img-fluid border rounded"
                    style={{
                      maxHeight: "280px",
                      objectFit: "cover",
                    }}
                  />
                </div>

                <div className="alert alert-light">
                  <strong>Image Information</strong>

                  <div className="small mt-1">
                    {imageInfo.width &&
                      imageInfo.height &&
                      `${imageInfo.width} × ${imageInfo.height}px`}

                    {imageInfo.sizeKB &&
                      ` • ${imageInfo.sizeKB} KB`}
                  </div>
                </div>
              </>
            ) : (
              <div className="alert alert-light text-center">
                No QR background image uploaded.
              </div>
            )}

            <div className="row g-3 align-items-end">
              <div className="col-12 col-lg-8">
                <label className="form-label">
                  Upload Background Image
                </label>

                <input
                  type="file"
                  accept="image/*"
                  className="form-control"
                  ref={fileInputRef}
                  onChange={(e) => setImageFile(e.target.files[0])}
                />
              </div>

              <div className="col-12 col-lg-4">
                {eventImage && (
                  <button
                    className="btn btn-outline-danger w-100"
                    onClick={handleDeleteImage}
                  >
                    🗑 Delete Background
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="card shadow-sm mb-4">
          <div className="card-header bg-white">
            <strong>🗺️ Event Map Image</strong>
          </div>

          <div className="card-body">
            <p className="text-muted">
              Upload a public event map. Sponsor map markers can reference booth numbers,
              spaces, or labels shown on this map.
            </p>

            {eventMap ? (
              <div className="text-center mb-4">
                <img
                  src={eventMap}
                  alt="Event Map"
                  className="img-fluid border rounded"
                  style={{
                    maxHeight: "500px",
                    objectFit: "contain",
                  }}
                />
              </div>
            ) : (
              <div className="alert alert-light text-center">
                No event map uploaded.
              </div>
            )}

            <div className="row g-3 align-items-end">
              <div className="col-12 col-lg-8">
                <label className="form-label">
                  Upload Event Map
                </label>

                <input
                  type="file"
                  accept="image/*"
                  className="form-control"
                  ref={mapInputRef}
                  onChange={(e) => setMapFile(e.target.files[0])}
                />
              </div>

              <div className="col-12 col-lg-4">
                {eventMap && (
                  <button
                    className="btn btn-outline-danger w-100"
                    onClick={handleDeleteMap}
                  >
                    🗑 Delete Event Map
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="card shadow-sm">
          <div className="card-body d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3">
            <div>
              <strong>Save Event Images</strong>

              <div className="small text-muted">
                Last saved: {lastSavedText}
              </div>
            </div>

            <button
              className="btn btn-primary"
              onClick={handleUpload}
              disabled={saving || (!imageFile && !logoFile && !mapFile)}
            >
              {saving ? "Saving..." : "💾 Save Images"}
            </button>
          </div>
        </div>
      </div>
    </section>
  );

};

export default SettingsImages;